import { useCallback, useEffect, useRef, useState } from 'react';
import {
  auditsAPI, intelligenceAPI,
  type AuditAnswerInput, type AuditDetail, type AuditDetailsInput, type AuditNote, type AuditOutcome, type AuditPhoto, type QualificationTemplate,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { toNumericAnswer } from '../../utils/answerValues';
import { saveBlob } from '../../utils/fileDownload';
import type { ResponseMap } from '../qualification-detail/types';
import { apiError, apiErrorAsync, apiErrorCode } from '../audits/useAuditActions';

/** Quiet time after the last edit before answers are saved. */
const AUTOSAVE_DELAY_MS = 1500;

/** Why the audit did not load: gone / no access, Audit Lite off, no connection, or a server fault. */
export type AuditLoadError = 'not_found' | 'off' | 'offline' | 'failed';

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/** Why a file cannot be uploaded as a photo, before sending it (null = fine). */
export function photoProblem(file: File): string | null {
  if (!PHOTO_TYPES.includes(file.type)) return `${file.name}: photos must be JPG, PNG or GIF (or a PDF).`;
  if (file.size > PHOTO_MAX_BYTES) return `${file.name} is over 10 MB — take it at a lower resolution.`;
  return null;
}
export type AnswerValue = string | number | boolean | string[];

type Pending = Record<number, AuditAnswerInput>;

export interface CompleteResult {
  ok: boolean;
  /** The completed audit (valid until etc.). */
  detail?: AuditDetail;
  /** Required checks the server still finds unanswered. */
  missing?: number[];
  /** Why it failed, for the dialog. */
  error?: string;
}

/** `newer` wins field by field over `older` (a failed batch put back under later edits). */
function mergePending(older: Pending, newer: Pending): Pending {
  const merged: Pending = { ...older };
  Object.values(newer).forEach((entry) => {
    merged[entry.question] = { ...(merged[entry.question] ?? {}), ...entry };
  });
  return merged;
}

/** The answer fields an input change writes, by question type (same mapping as the supplier form). */
function valueFields(value: AnswerValue, questionType: string): Partial<AuditAnswerInput> {
  switch (questionType) {
    case 'text':
    case 'textarea':
      return { text_value: String(value) };
    case 'number':
    case 'rating':
      return { number_value: toNumericAnswer(value) };
    case 'date':
      return { date_value: value ? String(value) : null };
    case 'yes_no':
      return { boolean_value: Boolean(value) };
    case 'single_choice':
      return { choice_value: [String(value)] };
    case 'multiple_choice':
      return { choice_value: value as string[] };
    default:
      return { text_value: String(value) };
  }
}

function toResponseMap(detail: AuditDetail): ResponseMap {
  const map: ResponseMap = {};
  detail.responses.forEach((r) => { map[r.question] = r; });
  return map;
}

/**
 * Everything the audit screen needs: the audit, its template, the answers with
 * comments and findings, and a debounced autosave that sends only what changed.
 *
 * Answering "No" flags the check as a finding (tap the flag again to undo);
 * switching back to "Yes" clears a flag the "No" set, unless a comment was written.
 *
 * @example const audit = useAuditDetail(42); audit.setAnswer(q.id, false, 'yes_no');
 * @param id - the audit id from the route
 * @returns audit, template, answers, photos, save state, and the edit / photo / complete / reopen / notes /
 *   archive / restore / delete handlers
 */
export function useAuditDetail(id: number) {
  const { user } = useAuth();
  const isCustomer = user?.platform === 'CUSTOMER';
  const [audit, setAudit] = useState<AuditDetail | null>(null);
  const [template, setTemplate] = useState<QualificationTemplate | null>(null);
  const [answers, setAnswers] = useState<ResponseMap>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<AuditLoadError | null>(null);
  /** Why the last autosave failed (shown beside Retry). */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [uploading, setUploading] = useState<Record<number, boolean>>({});
  const pending = useRef<Pending>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chain = useRef<Promise<boolean>>(Promise.resolve(true));
  const autoFlagged = useRef<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    let stage: 'audit' | 'template' = 'audit';
    try {
      const detail = await auditsAPI.get(id);
      stage = 'template';
      const tpl = (await intelligenceAPI.getTemplate(detail.template)).data;
      setAudit(detail);
      setTemplate(tpl);
      setAnswers(toResponseMap(detail));
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number } })?.response;
      if (!res) setError('offline');
      else if (apiErrorCode(err) === 'audit_lite_off') setError('off');
      // Only the audit's own 404 means "not found"; a missing template is a fault, not a missing audit.
      else if (res.status === 404 && stage === 'audit') setError('not_found');
      else setError('failed');
      if (res && res.status !== 404) console.error('Failed to load audit:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const loadRef = useRef(load);
  loadRef.current = load;

  /** Re-read the audit (counts, actions, answer ids) without touching unsaved edits. */
  const refresh = useCallback(async (): Promise<AuditDetail | null> => {
    try {
      const detail = await auditsAPI.get(id);
      setAudit(detail);
      setAnswers((prev) => {
        const next = { ...prev };
        detail.responses.forEach((r) => {
          next[r.question] = pending.current[r.question]
            ? { ...prev[r.question], id: r.id, question: r.question }
            : r;
        });
        return next;
      });
      return detail;
    } catch (err: unknown) {
      console.error('Failed to refresh audit:', err);
      return null;
    }
  }, [id]);

  const saveBatch = useCallback(async (): Promise<boolean> => {
    const batch = pending.current;
    if (Object.keys(batch).length === 0) return true;
    pending.current = {};
    setSaveStatus('saving');
    try {
      await auditsAPI.saveAnswers(id, Object.values(batch));
      setSaveStatus('saved');
      setSaveError(null);
      setLastSavedAt(new Date());
      return true;
    } catch (err: unknown) {
      console.error('Audit autosave failed:', err);
      const reason = apiError(err, 'Your answers could not be saved.');
      if (apiErrorCode(err) === 'invalid_state') {
        // Completed or archived elsewhere: retrying can never work — show the audit as it now is.
        pending.current = {};
        setSaveStatus('idle');
        setSaveError(null);
        toast.error(`${reason} Your last change was not saved.`);
        void loadRef.current();
        return false;
      }
      pending.current = mergePending(batch, pending.current);
      setSaveError(reason);
      setSaveStatus('error');
      return false;
    } finally {
      setPendingCount(Object.keys(pending.current).length);
    }
  }, [id]);

  /** Save now (Retry, before completing, before raising an action). Saves run one at a time, in order. */
  const flush = useCallback((): Promise<boolean> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    chain.current = chain.current.then(saveBatch, saveBatch);
    return chain.current;
  }, [saveBatch]);

  const queue = useCallback((questionId: number, fields: Partial<AuditAnswerInput>) => {
    pending.current = {
      ...pending.current,
      [questionId]: { ...(pending.current[questionId] ?? {}), ...fields, question: questionId },
    };
    setPendingCount(Object.keys(pending.current).length);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, AUTOSAVE_DELAY_MS);
  }, [flush]);

  const setAnswer = useCallback((questionId: number, value: AnswerValue, questionType: string) => {
    const fields: Partial<AuditAnswerInput> = valueFields(value, questionType);
    const current = answers[questionId];
    if (questionType === 'yes_no' && value === false && !current?.flagged) {
      fields.flagged = true;
      autoFlagged.current.add(questionId);
    } else if (questionType === 'yes_no' && value === true && autoFlagged.current.has(questionId)
      && !(current?.reviewer_notes ?? '').trim()) {
      fields.flagged = false;
      autoFlagged.current.delete(questionId);
    }
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], question: questionId, ...fields } as ResponseMap[number],
    }));
    queue(questionId, fields);
  }, [answers, queue]);

  const setComment = useCallback((questionId: number, text: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], question: questionId, reviewer_notes: text } }));
    queue(questionId, { reviewer_notes: text });
  }, [queue]);

  const setFlag = useCallback((questionId: number, flagged: boolean) => {
    autoFlagged.current.delete(questionId);
    setAnswers((prev) => ({ ...prev, [questionId]: { ...prev[questionId], question: questionId, flagged } }));
    queue(questionId, { flagged });
  }, [queue]);

  const uploadFile = useCallback(async (questionId: number, file: File) => {
    setUploading((prev) => ({ ...prev, [questionId]: true }));
    try {
      const saved = await auditsAPI.uploadFile(id, questionId, file);
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { ...prev[questionId], question: questionId, file_value: saved.file_path, file_name: saved.file_name },
      }));
      toast.success(`${saved.file_name} added`);
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not upload the file.'));
    } finally {
      setUploading((prev) => ({ ...prev, [questionId]: false }));
    }
  }, [id]);

  const downloadFile = useCallback(async (questionId: number) => {
    try {
      const blob = await auditsAPI.downloadFile(id, questionId);
      saveBlob(blob, answers[questionId]?.file_name || 'evidence', blob.type || 'application/octet-stream');
    } catch (err: unknown) {
      toast.error(await apiErrorAsync(err, 'Could not download the file.'));
    }
  }, [id, answers]);

  /** Clear the file of a file_upload check (the X on its file). */
  const removeAnswerFile = useCallback(async (questionId: number) => {
    try {
      await auditsAPI.removeFile(id, questionId);
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { ...prev[questionId], question: questionId, file_value: '', file_name: '' },
      }));
      toast.success('File removed');
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not remove the file.'));
    }
  }, [id]);

  /**
   * Add photos to a check, one upload at a time. Each one appears as soon as it
   * is in; each failure is toasted with the server's reason, and one toast
   * counts the ones that went in.
   */
  const uploadPhotos = useCallback(async (questionId: number, files: File[]) => {
    // Checked here first: a HEIC or 20 MB photo is refused before it is sent over a site connection.
    const usable = files.filter((file) => {
      const problem = photoProblem(file);
      if (problem) toast.error(problem);
      return !problem;
    });
    if (usable.length === 0) return;
    setUploading((prev) => ({ ...prev, [questionId]: true }));
    let added = 0;
    try {
      for (const file of usable) {
        try {
          const photo = await auditsAPI.uploadPhoto(id, questionId, file);
          added += 1;
          setAudit((prev) => (prev ? { ...prev, photos: [...(prev.photos ?? []), photo] } : prev));
        } catch (err: unknown) {
          toast.error(`${file.name}: ${apiError(err, 'Could not upload the photo.')}`);
        }
      }
    } finally {
      setUploading((prev) => ({ ...prev, [questionId]: false }));
    }
    if (added > 0) toast.success(`${added} ${added === 1 ? 'photo' : 'photos'} added`);
  }, [id]);

  /** Delete one photo. Resolves true once it is gone (a failure is toasted). */
  const deletePhoto = useCallback(async (photoId: number): Promise<boolean> => {
    try {
      await auditsAPI.deletePhoto(id, photoId);
      setAudit((prev) => (prev ? { ...prev, photos: (prev.photos ?? []).filter((p) => p.id !== photoId) } : prev));
      toast.success('Photo deleted');
      return true;
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not delete the photo.'));
      return false;
    }
  }, [id]);

  const downloadPhoto = useCallback(async (photo: AuditPhoto) => {
    try {
      const blob = await auditsAPI.downloadPhoto(id, photo.id);
      saveBlob(blob, photo.file_name || 'photo', blob.type || 'application/octet-stream');
    } catch (err: unknown) {
      toast.error(await apiErrorAsync(err, 'Could not download the photo.'));
    }
  }, [id]);

  /** The saved answer id for a check (saving first), so an action can point at its finding. */
  const responseIdFor = useCallback(async (questionId: number): Promise<number | null> => {
    await flush();
    const known = answers[questionId]?.id;
    if (known) return known;
    const detail = await refresh();
    return detail?.responses.find((r) => r.question === questionId)?.id ?? null;
  }, [answers, flush, refresh]);

  /** Complete = approve. Never toasts a failure: the dialog shows `error` / `missing` inline. */
  const complete = useCallback(async (outcome: AuditOutcome, summary: string): Promise<CompleteResult> => {
    if (!(await flush())) {
      return { ok: false, error: 'Some answers are not saved yet. Check your connection, then Retry saving before completing.' };
    }
    try {
      const detail = await auditsAPI.complete(id, outcome, summary);
      setAudit(detail);
      setAnswers(toResponseMap(detail));
      return { ok: true, detail };
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { missing_required?: number[] } } })?.response?.data;
      if (data?.missing_required?.length) return { ok: false, missing: data.missing_required };
      return { ok: false, error: apiError(err, 'Could not complete the audit.') };
    }
  }, [flush, id]);

  /** Change name / date / location / lead auditor. Resolves null on success, else the reason (shown inline). */
  const updateDetails = useCallback(async (payload: AuditDetailsInput): Promise<string | null> => {
    try {
      const detail = await auditsAPI.updateDetails(id, payload);
      setAudit(detail);
      toast.success('Audit details saved');
      return null;
    } catch (err: unknown) {
      return apiError(err, 'Could not save the audit details.');
    }
  }, [id]);

  /** Reopen with a reason. Resolves null on success, else the reason it failed (shown inline). */
  const reopen = useCallback(async (reason: string): Promise<string | null> => {
    try {
      const detail = await auditsAPI.reopen(id, reason);
      setAudit(detail);
      toast.success('Audit reopened — you can change the answers again.');
      return null;
    } catch (err: unknown) {
      return apiError(err, 'Could not reopen the audit.');
    }
  }, [id]);

  const addNote = useCallback(async (body: string): Promise<boolean> => {
    try {
      const note: AuditNote = await auditsAPI.addNote(id, body);
      // Newest first, the order the server returns the trail in.
      setAudit((prev) => (prev ? { ...prev, notes: [note, ...prev.notes] } : prev));
      return true;
    } catch (err: unknown) {
      toast.error(apiError(err, 'Could not add the note.'));
      return false;
    }
  }, [id]);

  /** Archive (flushing queued answers first). Resolves null on success, else the reason (shown in the dialog). */
  const archive = useCallback(async (): Promise<string | null> => {
    if (!(await flush())) return 'Some answers are not saved yet. Retry saving, then archive the audit.';
    try {
      setAudit(await auditsAPI.archive(id));
      toast.success('Audit archived');
      return null;
    } catch (err: unknown) {
      return apiError(err, 'Could not archive the audit.');
    }
  }, [flush, id]);

  /** Restore an archived audit. Resolves null on success, else the reason it failed. */
  const unarchive = useCallback(async (): Promise<string | null> => {
    try {
      setAudit(await auditsAPI.unarchive(id));
      toast.success('Audit restored');
      return null;
    } catch (err: unknown) {
      return apiError(err, 'Could not restore the audit.');
    }
  }, [id]);

  /** Delete for good (in progress only). Queued answers are dropped, not saved to a deleted audit. */
  const remove = useCallback(async (): Promise<string | null> => {
    try {
      await auditsAPI.remove(id);
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      pending.current = {};
      setPendingCount(0);
      toast.success('Audit deleted');
      return null;
    } catch (err: unknown) {
      return apiError(err, 'Could not delete the audit.');
    }
  }, [id]);

  // Leaving the page: save what is queued (and say so if that fails); warn on
  // refresh / tab close while anything is unsaved.
  useEffect(() => () => {
    if (!Object.keys(pending.current).length) return;
    void flush().then((ok) => {
      if (!ok) toast.error('Some audit answers could not be saved. Open the audit again and check them.');
    });
  }, [flush]);
  useEffect(() => {
    if (pendingCount === 0 && saveStatus !== 'saving') return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingCount, saveStatus]);

  // An archived audit is read-only on the server (answers, photos, details, actions, notes) until restored.
  const archived = Boolean(audit?.archived);
  const canEdit = Boolean(isCustomer && audit?.status === 'in_progress' && !archived);

  return {
    audit, template, answers, photos: audit?.photos ?? [], loading, error, reload: load, refresh,
    isCustomer, canEdit, canComplete: Boolean(audit?.can_complete) && !archived,
    /** Raise / edit actions and add follow-up notes. */
    canFollowUp: isCustomer && !archived,
    canArchive: Boolean(audit?.can_archive),
    saveStatus, saveError, lastSavedAt, hasUnsaved: pendingCount > 0, unsavedCount: pendingCount, flush,
    uploading, setAnswer, setComment, setFlag, uploadFile, downloadFile, removeAnswerFile,
    uploadPhotos, deletePhoto, downloadPhoto, responseIdFor,
    complete, reopen, addNote, updateDetails, archive, unarchive, remove,
  };
}
